import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DeleteDialog, Modal } from "components";
import PanelSegment from "components/panels/PanelSegment/PanelSegment";
import { MainPanelOrSectionPanel } from "components/panels";
import { useValidateIsNotEmptyArray } from "formvalidation/hooks";
import ProductEdit from "Products/ProductEdit";
import ProductsTable from "Products/ProductsTable";
import React, { useState } from "react";
import { useShortcutPanel } from "components/Shortcuts/hooks";

interface Props {
  documentType: server.dto.DocumentTypes | "show"
  documentId: number
  productsPanel: server.dto.ProductsPanel
  validatorKey?: string
  useSectionPanel: boolean
  onRefetchedProducts?: () => void;
}

export const getProductsByParentQueryKey = (documentType: server.dto.MainAreas, documentId: number) => ["productsByParent", documentType, documentId];

export const useProductsByParentQuery = (documentType: server.dto.MainAreas, documentId: number, initialData: server.dto.ProductsByParent) => {
  return useQuery({
    queryFn: async () => window.CoreApi.Product.ListByParent({ mainArea: documentType, parentId: documentId }).then(r => r.data.data),
    queryKey: getProductsByParentQueryKey(documentType, documentId),
    initialData: initialData,
    keepPreviousData: true,
    refetchOnMount: false
  });
}

const ProductsPanel = (props: Props) => {
  const [editingProduct, setEditingProduct] = useState<server.dto.Product>(undefined);
  const [deletingProduct, setDeletingProduct] = useState<server.dto.Product>(undefined);

  const productsByParentQueryKey = getProductsByParentQueryKey(props.documentType, props.documentId);
  const productsByParentQuery = useProductsByParentQuery(props.documentType, props.documentId, props.productsPanel.data);

  const queryClient = useQueryClient();
  const reorderProductsMutation = useMutation({
    mutationFn: async (productsByParent: server.dto.ProductsByParent) => {
      return window.CoreApi.Product.StoreSorted({
        mainArea: props.documentType,
        parentId: props.documentId,
        productIds: productsByParent.collection.map(p => p.productId)
      });
    },
    mutationKey: ["reorderProducts", props.documentType, props.documentId],
    onMutate: async (newProductsByParent: server.dto.ProductsByParent) => {
      await queryClient.cancelQueries(productsByParentQueryKey);
      const previousData = queryClient.getQueryData(productsByParentQueryKey);
      queryClient.setQueryData(productsByParentQueryKey, newProductsByParent);
      return { previousData };
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(productsByParentQueryKey, context.previousData);
    },
    onSettled: () => {
      queryClient.invalidateQueries(productsByParentQueryKey);
    }
  });

  const duplicateProductMutation = useMutation({
    mutationFn: async (product: server.dto.Product) => window.CoreApi.Product.Create({
      costsPricePerUnit: product.costs?.pricePerUnit.amount,
      costsVatRateId: product.costs?.vatRate.vatRateId,
      date: product.date,
      description: product.description,
      ledgerNumber: product.ledgerNumber,
      mainArea: props.documentType,
      parentId: props.documentId,
      productType: product.productType.value,
      quantity: product.quantity,
      salesPricePerUnit: product.sales?.pricePerUnit.amount,
      salesVatRateId: product.sales?.vatRate.vatRateId,
      title: product.title,
      unit: product.unit
    })
  });

  const duplicateProductMutationWithInstr = useMutation({
    mutationFn: window.CoreApi.Product.Create
  });

  const onProductCopyClicked = async (product: server.dto.Product) => {
    if (duplicateProductMutation.isLoading) return;
    await duplicateProductMutation.mutateAsync(product);
    productsByParentQuery.refetch();
    props.onRefetchedProducts?.();
  }

  const onProductDeleteClicked = (product: server.dto.Product) => {
    setDeletingProduct(product);
  };

  const onProductDeleted = async () => {
    const res = await window.CoreApi.Product.Delete({ productId: deletingProduct.productId });
    if (res.data.hasErrors) {
      window.displayGenericModal(RESX.ExceptionMessage.ThereIsAProblem, RESX.ExceptionMessage.ThereWasAnError, "tiny", "error");
      return;
    }

    productsByParentQuery.refetch();
    props.onRefetchedProducts?.();
    setDeletingProduct(undefined);
  };

  const productsValidation = useValidateIsNotEmptyArray({
    key: props.validatorKey,
    title: RESX.Product.Products,
    value: productsByParentQuery.data.collection
  })

  const { refToScrollTo } = useShortcutPanel({
    type: "product",
    label: props.productsPanel.config.headerLabel,
    totalItems: productsByParentQuery.data.collection.length ?? 0,
    onAddClicked: props.productsPanel.config.canModify ? () => { setEditingProduct(null); } : undefined
  });


  return <>
    <MainPanelOrSectionPanel
      useSectionPanel={props.useSectionPanel}
      title={props.productsPanel.config.headerLabel ?? RESX.Product.Products}
      action={<>
        <span ref={refToScrollTo}></span>
        {props.productsPanel.config.canModify && (
          <PanelSegment.Button onClick={() => { setEditingProduct(null); }} label={RESX.GeneralLabels.resxNew} type="secondary" />
        )}
      </>}
      hasData={productsByParentQuery.data.collection.length > 0}
      panelRef={productsValidation.ref}
      legacyPanelProps={{ panelClassName: "s1_related-feature-panel" }}
      hasError={productsValidation.error.hasError}
    >

      {productsByParentQuery.data.collection.length > 0 && <ProductsTable
        onEditClicked={setEditingProduct}
        onDeleteClicked={onProductDeleteClicked}
        onCopyClicked={onProductCopyClicked}
        productsByParent={productsByParentQuery.data}
        productsPanelConfig={props.productsPanel.config}
        onProductsOrderChanged={reorderProductsMutation.mutate}
      />}

    </MainPanelOrSectionPanel>

    <DeleteDialog
      isOpen={deletingProduct != null}
      message={RESX.GeneralWarnings.AreYouSure}
      handleClose={() => { setDeletingProduct(null); }}
      onDelete={onProductDeleted} />

    <Modal
      isOpen={editingProduct !== undefined}
      title={editingProduct == null ? RESX.Product.NewProduct : RESX.Product.EditProduct}
      width="huge"
      height={780}
      handleClose={() => { setEditingProduct(undefined); }}
    >
      <ProductEdit
        initialProduct={editingProduct}
        productsConfig={props.productsPanel.config}
        productsByParent={productsByParentQuery.data}
        onProductSaved={() => {
          productsByParentQuery.refetch();
          props.onRefetchedProducts?.();
          setEditingProduct(undefined);
        }}
        saveAsDuplicateMutation={duplicateProductMutationWithInstr}
        mainArea={props.documentType}
      />
    </Modal>
  </>
}

export default ProductsPanel;